import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Plus, Ban, Download } from "lucide-react";
import { api } from "@/lib/api";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { formatMoney, formatDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { PaymentDialog } from "./PaymentDialog";
import { VoidDialog } from "./VoidDialog";

export const PaymentsTab = () => {
  const { t } = useT();
  const { can } = useAuth();
  const [dialog, setDialog] = useState(null); // "payment" | "advance"
  const [voidTarget, setVoidTarget] = useState(null); // { kind, id }

  const { data: payments } = useQuery({
    queryKey: ["payments"],
    queryFn: async () => (await api.get("/payments", { params: { page_size: 100 } })).data.items,
  });
  const { data: advances } = useQuery({
    queryKey: ["advances"],
    queryFn: async () => (await api.get("/advances", { params: { page_size: 100 } })).data.items,
  });

  return (
    <div data-testid="finance-payments-tab" className="space-y-6">
      <div className="flex justify-end gap-2">
        <Button data-testid="new-advance-button" variant="outline" onClick={() => setDialog("advance")}>
          <Plus className="mr-1.5 h-4 w-4" />{t("finance.payments.new_advance")}
        </Button>
        {can("finance", "create") && (
          <Button data-testid="new-payment-button" className="bg-brand hover:bg-brand-hover" onClick={() => setDialog("payment")}>
            <Plus className="mr-1.5 h-4 w-4" />{t("finance.payments.new_payment")}
          </Button>
        )}
      </div>

      <div className="rounded-[10px] border border-line bg-white">
        <p className="border-b border-line px-4 py-2.5 text-[13px] font-semibold text-ink">
          {t("finance.payments.history")}
        </p>
        {!payments?.length ? (
          <p className="p-6 text-center text-[13px] text-ink-tertiary">{t("finance.payments.no_payments")}</p>
        ) : (
          <table className="w-full text-[12.5px]" data-testid="payments-table">
            <thead className="bg-surface-sunken text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
              <tr>
                <th className="px-3 py-2 text-left">{t("finance.payments.date")}</th>
                <th className="px-3 py-2 text-left">{t("finance.payments.sub")}</th>
                <th className="px-3 py-2 text-left">{t("finance.payments.amount")}</th>
                <th className="px-3 py-2 text-left">{t("finance.payments.type")}</th>
                <th className="px-3 py-2 text-left">{t("finance.payments.orders_covered")}</th>
                <th className="px-3 py-2 text-left">{t("finance.payments.registered_by")}</th>
                <th className="px-3 py-2 text-left" />
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id} className="border-b border-line" data-testid={`payment-row-${p.id}`}>
                  <td className="px-3 py-1.5">{formatDate(p.fecha)}</td>
                  <td className="px-3 py-1.5">{p.sub_nombre}</td>
                  <td className="px-3 py-1.5 tabular-nums">{formatMoney(p.monto_cents)}</td>
                  <td className="px-3 py-1.5">
                    <span className="inline-flex flex-wrap items-center gap-1">
                      {p.anulado && <Badge variant="outline" className="text-state-critical">{t("finance.payments.voided")}</Badge>}
                      {p.es_contraasiento && (
                        <Badge data-testid={`payment-reversal-${p.id}`} variant="outline" className="text-state-warn">
                          {t("finance.payments.reversal")}
                        </Badge>
                      )}
                      {t(`finance.payments.types.${p.tipo}`)}
                    </span>
                  </td>
                  <td className="px-3 py-1.5 text-ink-secondary">
                    {p.es_a_cuenta
                      ? t("finance.payments.on_account")
                      : (p.work_order_allocations || []).map((a) => a.code).filter(Boolean).join(", ") || "—"}
                  </td>
                  <td className="px-3 py-1.5 text-ink-secondary">{p.created_by_nombre || "—"}</td>
                  <td className="px-3 py-1.5 text-right">
                    {!p.anulado && !p.contraasiento_de_id && can("finance", "update") && (
                      <button data-testid={`void-payment-${p.id}`} onClick={() => setVoidTarget({ kind: "payment", id: p.id })}
                        className="text-ink-tertiary hover:text-state-critical"><Ban className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="rounded-[10px] border border-line bg-white">
        <p className="border-b border-line px-4 py-2.5 text-[13px] font-semibold text-ink">
          {t("finance.payments.advances_title")}
        </p>
        {!advances?.length ? (
          <p className="p-6 text-center text-[13px] text-ink-tertiary">{t("finance.payments.no_advances")}</p>
        ) : (
          <table className="w-full text-[12.5px]" data-testid="advances-table">
            <thead className="bg-surface-sunken text-[11px] uppercase tracking-[0.04em] text-ink-tertiary">
              <tr>
                <th className="px-3 py-2 text-left">{t("finance.payments.date")}</th>
                <th className="px-3 py-2 text-left">{t("finance.payments.sub")}</th>
                <th className="px-3 py-2 text-left">{t("finance.payments.amount")}</th>
                <th className="px-3 py-2 text-left" />
              </tr>
            </thead>
            <tbody>
              {advances.map((a) => (
                <tr key={a.id} className="border-b border-line" data-testid={`advance-row-${a.id}`}>
                  <td className="px-3 py-1.5">{formatDate(a.fecha)}</td>
                  <td className="px-3 py-1.5">{a.sub_nombre}</td>
                  <td className="px-3 py-1.5 tabular-nums">{formatMoney(a.monto_cents)}</td>
                  <td className="px-3 py-1.5 text-right">
                    {!a.anulado && !a.contraasiento_de_id && can("finance", "update") && (
                      <button data-testid={`void-advance-${a.id}`} onClick={() => setVoidTarget({ kind: "advance", id: a.id })}
                        className="text-ink-tertiary hover:text-state-critical"><Ban className="h-3.5 w-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <PaymentDialog open={dialog === "payment"} onOpenChange={(v) => setDialog(v ? "payment" : null)} kind="payment" />
      <PaymentDialog open={dialog === "advance"} onOpenChange={(v) => setDialog(v ? "advance" : null)} kind="advance" />
      {voidTarget && (
        <VoidDialog open={Boolean(voidTarget)} onOpenChange={(v) => !v && setVoidTarget(null)} kind={voidTarget.kind} id={voidTarget.id} />
      )}
    </div>
  );
};
